import { Handle, Position} from "reactflow";
import styles from '../styles/nodes.module.css';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPaperPlane, faX, faTriangleExclamation, faCircleInfo, faShieldHalved, faPlay, faSpinner, faCheck} from "@fortawesome/free-solid-svg-icons";
import {useEffect, useState, useContext, useRef, useCallback} from "react";
import { useTranslation } from 'react-i18next';
import Modal from "../components/modal/modal";
import PhoneInput from "react-phone-input-2";
import Explorer from "../components/explorer/explorer";
import ConnectionManager from "../components/ConnectionManager";
import axios from "../AxiosInstance";
import MyContext from "../Context";
import {
    ACTION_TYPES,
    NOTIFICATION_ACTIONS,
    ACTION_HELP_TEXT,
    validateAction
} from "../utils/operationValidation";


export default function ActionNode({id, data}) {
    const { t } = useTranslation();
    const [actionType, setActionType] = useState(data.action_type);
    const [actionData, setActionData] = useState(data.action_data_1);
    const [actionData2, setActionData2] = useState(data.action_data_2);
    const [actionData3, setActionData3] = useState(data.action_data_3);
    const [actionData4, setActionData4] = useState(data.action_data_4); // Variable ID for widget
    const [actionData5, setActionData5] = useState(data.action_data_5); // Widget PK (for updating existing widget)
    const [actionData6, setActionData6] = useState(data.action_data_6); // Sampling for graphs
    const [showModal, setShowModal] = useState(false);
    const [showDeviceExplorer, setShowDeviceExplorer] = useState(false);
    const project = JSON.parse(localStorage.getItem('project'));
    const [mentions, setMentions] = useState([]);
    const textareaRef = useRef(null);
    const [variablesList, setVariablesList] = useState([]);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionFilter, setMentionFilter] = useState('');
    const [mentionStartPos, setMentionStartPos] = useState(0);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const dropdownRef = useRef(null);
    const apiBodyEditorRef = useRef(null);
    const [activeEditor, setActiveEditor] = useState(null); // 'message', 'apiBody'
    const [activeDataIndex, setActiveDataIndex] = useState(3); // Default to message
    const { dashboards, corporateGroup, systemSettings } = useContext(MyContext);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const widgetTypes = [
        { id: 'variable', name: t('operations.actionNode.variableValue') },
        { id: 'graph', name: t('operations.actionNode.graph') }
    ];
    const samplingOptions = [
        { id: 0, name: t('operations.actionNode.oneHour') },
        { id: 1, name: t('operations.actionNode.twentyFourHours') },
        { id: 2, name: t('operations.actionNode.sevenDays') }
    ];
    const [widgetAdded, setWidgetAdded] = useState(false);
    const [mqttJsonError, setMqttJsonError] = useState(null);
    const [apiBodyJsonError, setApiBodyJsonError] = useState(null);
    const [booleanFormat, setBooleanFormat] = useState(data.boolean_format || 'raw');

    const tryParseJson = (text) => {
        if (!text || !text.trim()) return null;
        try {
            const normalized = text
                .replace(/"@[^"]+"/g, '"__x__"')               // "@mention" inside JSON strings
                .replace(/@[^@\n,}\]"]+(\([^)]*\))?/g, '"__x__"'); // bare @mention (allows spaces in name)
            JSON.parse(normalized);
            return null;
        } catch (e) {
            return e.message;
        }
    };
    const [widgetUpdated, setWidgetUpdated] = useState(false);
    const [widgetError, setWidgetError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [variableSearchFilter, setVariableSearchFilter] = useState('');

    // MQTT & API Connection states
    const [mqttConnections, setMqttConnections] = useState([]);
    const [apiConnections, setApiConnections] = useState([]);
    const [selectedMqttConnection, setSelectedMqttConnection] = useState(data.mqtt_connection_id || null);
    const [selectedApiConnection, setSelectedApiConnection] = useState(data.api_connection_id || null);
    const [showConnectionManager, setShowConnectionManager] = useState(false);
    const [connectionManagerType, setConnectionManagerType] = useState(null); // 'mqtt' or 'api'
    const [httpMethods] = useState(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']);
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState(data.action_data_4 || '');

    // Validation state for notification actions
    const [validationStatus, setValidationStatus] = useState({ valid: true, message: '', type: null });

    // Check if this action is connected to a valid comparison operation
    useEffect(() => {
        // Skip validation if function not available yet
        if (!data.getConnectedOperations) {
            console.log('getConnectedOperations not available yet for action:', id);
            return;
        }

        if (NOTIFICATION_ACTIONS.includes(actionType)) {
            const connectedOps = data.getConnectedOperations(id);
            console.log('Validation check for action', id, '- Connected ops:', connectedOps.length, connectedOps);
            const validation = validateAction({ data: { action_type: actionType } }, connectedOps);
            console.log('Validation result:', validation);
            setValidationStatus(validation);
            // Update data for parent component to access validation status
            data.isValid = validation.valid;
            data.validationMessage = validation.message;
        } else if (actionType === ACTION_TYPES.ADD_TO_DASHBOARD) {
            setValidationStatus({ valid: true, message: '', type: null });
            data.isValid = true;
        }
    }, [actionType, data.connectionVersion, id, data.getConnectedOperations]);

    useEffect(() => {
        setActionType(data.action_type);
        setActionData(data.action_data_1);
        setActionData2(data.action_data_2);
        setActionData3(data.action_data_3);
        setActionData4(data.action_data_4);
        setActionData5(data.action_data_5);
        setActionData6(data.action_data_6);
        setBooleanFormat(data.boolean_format || 'raw');
    }, [data]);

    // Resetear widget type si la variable seleccionada es bool/string y tenía graph seleccionado
    useEffect(() => {
        if (actionType === '2' && actionData4 && actionData2 === 'graph') {
            const selectedVariable = variablesList?.find(v => v.id === actionData4);
            const selectedVarType = selectedVariable?.type;
            const isBoolean = selectedVarType === 5;
            const isString = selectedVarType === 3;

            // Si es bool o string y tiene graph seleccionado, resetear a variable
            if (isBoolean || isString) {
                setActionData2('variable');
                data.action_data_2 = 'variable';
            }
        }
    }, [actionData4, variablesList, actionType, actionData2]);

    useEffect(() => {
        if(showModal) {
            // Sincronizar datos del nodo al abrir el modal
            setActionType(data.action_type);
            setActionData(data.action_data_1);
            setActionData2(data.action_data_2);
            setActionData3(data.action_data_3);
            setActionData4(data.action_data_4);
            setActionData5(data.action_data_5);
            setActionData6(data.action_data_6);
            setSelectedMqttConnection(data.mqtt_connection_id || null);
            setSelectedApiConnection(data.api_connection_id || null);
            setBooleanFormat(data.boolean_format || 'raw');

            // Limpiar estados de feedback al abrir el modal
            setWidgetError('');
            setWidgetAdded(false);
            setWidgetUpdated(false);
            const optionsList = [];
            const varsList = [];
            axios.get(`device/?project_id=${project.id}`).then((response) => {
                let devices = response.data;
                devices.forEach((device) => {
                    const deviceName = device.name;
                    let variables = device.variables;
                    variables.forEach((variable) => {
                        const mentionName = deviceName + " " + variable.variable.name + " (" + variable.id + ")";
                        optionsList.push({
                            text: mentionName,
                            value: variable.id.toString()
                        });
                        // Para el selector de variables en Add to Dashboard
                        varsList.push({
                            id: variable.id,
                            name: `${deviceName} - ${variable.variable.name}`,
                            type: variable.variable.var_type
                        });
                    })
                })

                // Agregar variables de resultado de operaciones
                if (data.getResultVariables) {
                    const resultVars = data.getResultVariables();
                    console.log('Result variables from getResultVariables:', resultVars);
                    resultVars.forEach((resultVar) => {
                        optionsList.push(resultVar);
                        // También agregar al selector de variables para Add to Dashboard
                        varsList.push({
                            id: resultVar.value,
                            name: resultVar.text,
                            type: resultVar.var_type // Las result vars también tienen var_type
                        });
                    });
                } else {
                    console.log('getResultVariables function not available in data');
                }

                // Agregar @device_id como una mención especial (formato chip)
                optionsList.push({
                    text: 'device_id (device_id)',
                    value: 'device_id',
                    isSpecial: true
                });

                console.log('Final optionsList for mentions:', optionsList);

                setMentions(optionsList);
                setVariablesList(varsList);
            });

            // Load MQTT and API connections
            axios.get(`mqtt_connection/?project_id=${project.id}`).then((response) => {
                setMqttConnections(response.data.results || response.data || []);
            }).catch(() => setMqttConnections([]));

            axios.get(`api_connection/?project_id=${project.id}`).then((response) => {
                setApiConnections(response.data.results || response.data || []);
            }).catch(() => setApiConnections([]));

            // Load devices for @device_id selector
            axios.get(`device/?project_id=${project.id}`).then((response) => {
                setDevices(response.data.results || response.data || []);
            }).catch(() => setDevices([]));
        }
    }, [showModal]);

    // Filtrar menciones basándose en el texto escrito después de @
    const filteredMentions = mentions.filter(m =>
        m.text.toLowerCase().includes(mentionFilter.toLowerCase())
    );

    const handleActionChange = (event) => {
        const newActionType = event.target.value;
        setActionType(newActionType);
        data.action_type = newActionType;
    }

    const handleActionData = (event, index) => {
        const newData = event.target.value;
        if(index === 1) {
            setActionData(newData);
            data.action_data_1 = newData;
        } else if (index === 2) {
            setActionData2(newData);
            data.action_data_2 = newData;
        } else if (index === 3) {
            setActionData3(newData);
            data.action_data_3 = newData;
        } else if (index === 4) {
            setActionData4(newData);
            data.action_data_4 = newData;
        } else if (index === 5) {
            setActionData5(newData);
            data.action_data_5 = newData;
        } else if (index === 6) {
            setActionData6(newData);
            data.action_data_6 = newData;
        }
    }

    // Referencias para los editores con chips
    const chipEditorRef = useRef(null);
    const topicEditorRef = useRef(null);
    const endpointEditorRef = useRef(null);

    // Función para convertir texto con @mentions a HTML con chips
    const textToHtml = useCallback((text) => {
        if (!text) return '';
        // Escapar HTML básico
        let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        // Reemplazar @mentions con chips
        // Capturar @texto hasta el paréntesis con ID, ej: @Device Variable (123) o @VarName (result_abc)
        html = html.replace(/@([^@\n]+?\s*\([^)]+\))/g, (match, varName) => {
            // Las variables internas tienen formato (result_xxx)
            const isResultVar = varName.includes('(result_');
            const chipClass = isResultVar ? `${styles.variableChip} ${styles.variableChipResult}` : styles.variableChip;
            return `<span class="${chipClass}" contenteditable="false" data-variable="${varName}">${varName}</span>`;
        });
        // Convertir saltos de línea a <br>
        html = html.replace(/\n/g, '<br>');
        return html;
    }, []);

    // Función para convertir HTML con chips a texto plano con @mentions
    const htmlToText = useCallback((element) => {
        if (!element) return '';
        let text = '';
        element.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent;
            } else if (node.nodeName === 'BR') {
                text += '\n';
            } else if (node.nodeName === 'SPAN' && node.dataset.variable) {
                text += `@${node.dataset.variable}`;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                text += htmlToText(node);
            }
        });
        return text;
    }, []);

    // Guardar posición del cursor
    const saveCaretPosition = useCallback((element) => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return null;
        const range = selection.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        return preCaretRange.toString().length;
    }, []);

    // Restaurar posición del cursor
    const restoreCaretPosition = useCallback((element, offset) => {
        if (offset === null) return;
        const selection = window.getSelection();
        const range = document.createRange();

        let currentOffset = 0;
        let found = false;

        const walkNodes = (node) => {
            if (found) return;
            if (node.nodeType === Node.TEXT_NODE) {
                if (currentOffset + node.length >= offset) {
                    range.setStart(node, offset - currentOffset);
                    range.collapse(true);
                    found = true;
                } else {
                    currentOffset += node.length;
                }
            } else {
                for (let child of node.childNodes) {
                    walkNodes(child);
                    if (found) break;
                }
            }
        };

        walkNodes(element);

        if (!found) {
            range.selectNodeContents(element);
            range.collapse(false);
        }

        selection.removeAllRanges();
        selection.addRange(range);
    }, []);

    // Manejar input en el editor de chips
    const handleChipEditorInput = useCallback((event) => {
        const editor = event.target;

        // Solo detectar @ para mostrar dropdown, NO actualizar estado aquí
        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        const range = selection.getRangeAt(0);

        // Obtener el texto completo hasta el cursor
        let textBeforeCursor = '';
        if (range.startContainer.nodeType === Node.TEXT_NODE) {
            textBeforeCursor = range.startContainer.textContent.substring(0, range.startOffset);
        } else if (range.startContainer.nodeType === Node.ELEMENT_NODE) {
            // Si es un elemento, obtener todo el texto del editor hasta el cursor
            const editorText = editor.textContent || '';
            textBeforeCursor = editorText.substring(0, saveCaretPosition(editor));
        }

        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
            // Mostrar dropdown si no hay espacios ni saltos de línea después del @
            if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                setMentionFilter(textAfterAt.trim());
                setShowMentionDropdown(true);
                setSelectedMentionIndex(0);

                const rect = editor.getBoundingClientRect();
                setDropdownPosition({
                    top: rect.height + 5,
                    left: 0
                });
                setMentionStartPos(lastAtIndex);
                return;
            }
        }

        setShowMentionDropdown(false);
        setMentionFilter('');
    }, [activeDataIndex, htmlToText, handleActionData, saveCaretPosition]);

    // Insertar chip de variable
    const insertVariableChip = useCallback((mention) => {
        // Determinar qué editor usar
        let editor;
        if (activeEditor === 'apiBody') {
            editor = apiBodyEditorRef.current;
        } else if (activeEditor === 'topic') {
            editor = topicEditorRef.current;
        } else if (activeEditor === 'endpoint') {
            editor = endpointEditorRef.current;
        } else {
            editor = chipEditorRef.current;
        }

        if (!editor) return;

        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        // Obtener texto actual y eliminar el @ parcial
        let text = htmlToText(editor);
        const cursorPos = saveCaretPosition(editor);

        // Encontrar el @ antes del cursor
        let atPos = -1;
        for (let i = cursorPos - 1; i >= 0; i--) {
            if (text[i] === '@') {
                atPos = i;
                break;
            }
        }

        if (atPos === -1) return;

        // Crear nuevo texto con la mención completa
        const beforeAt = text.substring(0, atPos);
        const afterCursor = text.substring(cursorPos);
        const newText = beforeAt + `@${mention.text} ` + afterCursor;

        handleActionData({ target: { value: newText } }, activeDataIndex);

        // Actualizar el editor con chips
        setTimeout(() => {
            if (editor) {
                editor.innerHTML = textToHtml(newText);
                editor.focus();
                // Posicionar cursor al final del chip insertado
                const newCursorPos = beforeAt.length + mention.text.length + 2;
                restoreCaretPosition(editor, newCursorPos);
            }
        }, 0);

        setShowMentionDropdown(false);
        setMentionFilter('');
        setSelectedMentionIndex(0);
    }, [activeEditor, activeDataIndex, htmlToText, textToHtml, saveCaretPosition, restoreCaretPosition, handleActionData]);

    // Manejar teclas en el editor de chips
    const handleChipEditorKeyDown = useCallback((event) => {
        if (!showMentionDropdown || filteredMentions.length === 0) {
            // Manejar backspace para eliminar chips completos
            if (event.key === 'Backspace') {
                const selection = window.getSelection();
                if (selection.rangeCount) {
                    const range = selection.getRangeAt(0);
                    if (range.collapsed && range.startOffset === 0) {
                        const prevSibling = range.startContainer.previousSibling ||
                            (range.startContainer.parentNode !== chipEditorRef.current && range.startContainer.parentNode.previousSibling);
                        if (prevSibling && prevSibling.dataset && prevSibling.dataset.variable) {
                            event.preventDefault();
                            prevSibling.remove();
                            const text = htmlToText(chipEditorRef.current);
                            handleActionData({ target: { value: text } }, 3);
                        }
                    }
                }
            }
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSelectedMentionIndex(prev =>
                prev < filteredMentions.length - 1 ? prev + 1 : 0
            );
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSelectedMentionIndex(prev =>
                prev > 0 ? prev - 1 : filteredMentions.length - 1
            );
        } else if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            insertVariableChip(filteredMentions[selectedMentionIndex]);
        } else if (event.key === 'Escape') {
            setShowMentionDropdown(false);
            setMentionFilter('');
        }
    }, [showMentionDropdown, filteredMentions, selectedMentionIndex, insertVariableChip, htmlToText, handleActionData]);

    // Inicializar editor con chips cuando se abre el modal
    useEffect(() => {
        if (!showModal) return;

        // Usar setTimeout para asegurar que el DOM esté renderizado
        setTimeout(() => {
            // Para Email (1), SMS (3), WhatsApp (4) - actualizar body/message usando chipEditorRef
            if ((actionType === '1' || actionType === '3' || actionType === '4') && chipEditorRef.current && actionData3) {
                chipEditorRef.current.innerHTML = textToHtml(actionData3);
            }

            // Para MQTT (5) - actualizar topic y message
            if (actionType === '5') {
                if (topicEditorRef.current && actionData) {
                    topicEditorRef.current.innerHTML = textToHtml(actionData);
                }
                if (chipEditorRef.current && actionData3) {
                    chipEditorRef.current.innerHTML = textToHtml(actionData3);
                }
            }

            // Para API Call (6) - actualizar endpoint y body
            if (actionType === '6') {
                if (endpointEditorRef.current && actionData2) {
                    endpointEditorRef.current.innerHTML = textToHtml(actionData2);
                }
                if (apiBodyEditorRef.current && actionData3) {
                    apiBodyEditorRef.current.innerHTML = textToHtml(actionData3);
                }
            }
        }, 100);
    }, [showModal, actionType, actionData, actionData2, actionData3, textToHtml]);

    // Función para insertar la mención seleccionada
    const insertMention = (mentionText) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const currentValue = actionData3 || '';

        // Reemplazar desde el @ hasta la posición actual
        const beforeMention = currentValue.substring(0, mentionStartPos);
        const afterCursor = currentValue.substring(textarea.selectionStart);
        const newValue = beforeMention + `@${mentionText} ` + afterCursor;

        handleActionData({target: {value: newValue}}, 3);
        setShowMentionDropdown(false);
        setMentionFilter('');
        setSelectedMentionIndex(0);

        // Restaurar foco y posición del cursor
        setTimeout(() => {
            textarea.focus();
            const newPosition = beforeMention.length + mentionText.length + 2;
            textarea.setSelectionRange(newPosition, newPosition);
        }, 0);
    };

    // Manejar cambios en el textarea para detectar @
    const handleBodyChange = (event) => {
        const value = event.target.value;
        const cursorPos = event.target.selectionStart;

        handleActionData(event, 3);

        // Buscar si hay un @ antes del cursor
        const textBeforeCursor = value.substring(0, cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
            // Solo mostrar dropdown si no hay espacios después del @
            if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                setMentionStartPos(lastAtIndex);
                setMentionFilter(textAfterAt);
                setShowMentionDropdown(true);
                setSelectedMentionIndex(0);

                // Calcular posición del dropdown
                const textarea = textareaRef.current;
                if (textarea) {
                    const rect = textarea.getBoundingClientRect();
                    setDropdownPosition({
                        top: rect.height + 5,
                        left: 0
                    });
                }
                return;
            }
        }

        setShowMentionDropdown(false);
        setMentionFilter('');
    };

    // Manejar teclas especiales en el textarea
    const handleKeyDown = (event) => {
        if (!showMentionDropdown || filteredMentions.length === 0) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSelectedMentionIndex(prev =>
                prev < filteredMentions.length - 1 ? prev + 1 : 0
            );
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSelectedMentionIndex(prev =>
                prev > 0 ? prev - 1 : filteredMentions.length - 1
            );
        } else if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            insertMention(filteredMentions[selectedMentionIndex].text);
        } else if (event.key === 'Escape') {
            setShowMentionDropdown(false);
            setMentionFilter('');
        }
    };

    const inputs = () => {
        if(actionType === '2') {
            // Determinar el tipo de la variable seleccionada
            const selectedVariable = variablesList?.find(v => v.id === actionData4);
            const selectedVarType = selectedVariable?.type;

            // Determinar si es booleana, string o numérica
            // var_type: 1=int, 2=float, 3=string, 4=datetime, 5=bool
            const isBoolean = selectedVarType === 5;
            const isString = selectedVarType === 3;
            const isNumeric = selectedVarType === 1 || selectedVarType === 2;

            // Filtrar opciones de widget según el tipo de variable
            const availableWidgetTypes = (isBoolean || isString)
                ? [{ id: 'variable', name: 'Variable Value' }]
                : widgetTypes; // Para numeric, mostrar ambas opciones

            return (
                <div>
                    <div className={styles.scratch_label}>{t('operations.actionNode.dashboard')}</div>
                    <select
                        className={styles.modalSelect}
                        value={actionData === null || actionData === undefined ? '' : actionData}
                        onChange={(event) => handleActionData(event, 1)}
                    >
                        <option value="">{t('operations.actionNode.selectDashboard')}</option>
                        {dashboards?.map((dashboard) => (
                            <option key={dashboard.id} value={dashboard.id}>
                                {dashboard.name}
                            </option>
                        ))}
                    </select>

                    <div className={styles.scratch_label}>{t('operations.actionNode.selectVariable')}</div>
                    <div className="mb-2">
                        <input
                            type="text"
                            className={styles.scratch_input}
                            placeholder={t('operations.actionNode.searchVariable')}
                            value={variableSearchFilter}
                            onChange={(e) => setVariableSearchFilter(e.target.value)}
                            style={{marginBottom: '8px'}}
                        />
                        {actionData4 && (
                            <div style={{
                                padding: '8px 12px',
                                backgroundColor: '#e0e7ff',
                                borderRadius: '6px',
                                marginBottom: '8px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <span style={{color: '#4338ca', fontWeight: 500}}>
                                    {variablesList?.find(v => v.id === actionData4)?.name || 'Selected variable'}
                                </span>
                                <span
                                    onClick={() => handleActionData({target: {value: ''}}, 4)}
                                    style={{cursor: 'pointer', color: '#6366f1', fontSize: '0.875rem'}}
                                >
                                    ✕ {t('operations.actionNode.clearVariable')}
                                </span>
                            </div>
                        )}
                        <div style={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            backgroundColor: '#fff'
                        }}>
                            {variablesList
                                ?.filter((variable) => {
                                    if (!variableSearchFilter) return true;
                                    return variable.name.toLowerCase().includes(variableSearchFilter.toLowerCase());
                                })
                                .slice(0, 50)  // Limit to first 50 results for performance
                                .map((variable) => (
                                    <div
                                        key={variable.id}
                                        onClick={() => {
                                            handleActionData({target: {value: variable.id}}, 4);
                                            setVariableSearchFilter('');
                                        }}
                                        style={{
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #f1f5f9',
                                            backgroundColor: actionData4 === variable.id ? '#f0f9ff' : '#fff'
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = actionData4 === variable.id ? '#f0f9ff' : '#fff'}
                                    >
                                        {variable.name}
                                    </div>
                                ))
                            }
                            {variablesList?.filter((variable) => {
                                if (!variableSearchFilter) return true;
                                return variable.name.toLowerCase().includes(variableSearchFilter.toLowerCase());
                            }).length === 0 && (
                                <div style={{padding: '12px', textAlign: 'center', color: '#94a3b8'}}>
                                    {t('operations.actionNode.noVariablesFound')}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mostrar Widget Type solo si hay una variable seleccionada */}
                    {actionData4 && (
                        <>
                            <div className={styles.scratch_label}>{t('operations.actionNode.widgetType')}</div>
                            <select
                                className={styles.modalSelect}
                                value={actionData2 || ''}
                                onChange={(event) => handleActionData(event, 2)}
                            >
                                <option value="">{t('operations.actionNode.selectWidgetType')}</option>
                                {availableWidgetTypes.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>

                            {/* Mostrar Sampling solo si el widget type es graph */}
                            {actionData2 === 'graph' && (
                                <>
                                    <div className={styles.scratch_label}>{t('operations.actionNode.sampling')}</div>
                                    <select
                                        className={styles.modalSelect}
                                        value={actionData6 === null || actionData6 === undefined ? '' : actionData6}
                                        onChange={(event) => handleActionData(event, 6)}
                                    >
                                        <option value="">{t('operations.actionNode.selectSampling')}</option>
                                        {samplingOptions.map((option) => (
                                            <option key={option.id} value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            )}
                        </>
                    )}

                    <div className={styles.scratch_label}>{t('operations.actionNode.widgetName')}</div>
                    <input
                        className={styles.scratch_input}
                        placeholder={t('operations.actionNode.widgetName')}
                        onChange={(event) => handleActionData(event, 3)}
                        value={actionData3 || ''}
                    />

                    {widgetError && (
                        <div style={{color: '#dc3545', fontSize: '12px', marginTop: '10px'}}>
                            {widgetError}
                        </div>
                    )}
                    {widgetAdded && (
                        <div style={{color: '#28a745', fontSize: '12px', marginTop: '10px'}}>
                            {t('operations.nodes.widgetAddedSuccess')}
                        </div>
                    )}
                    {widgetUpdated && (
                        <div style={{color: '#28a745', fontSize: '12px', marginTop: '10px'}}>
                            {t('operations.nodes.widgetUpdatedSuccess')}
                        </div>
                    )}
                </div>
            );
        }
        else if(actionType === '3' || actionType === '4') {
            return (
                <div>
                    <div className={styles.scratch_label}>{t('operations.actionNode.to')}</div>
                    <PhoneInput country={'sn'} value={actionData2} onChange={phone => {
                        setActionData2(phone);
                        data.action_data_2 = phone;
                    }}/>
                    <div className={styles.scratch_label}>{t('operations.actionNode.body')}</div>
                    <div className={styles.mentionContainer}>
                        <div
                            ref={chipEditorRef}
                            className={styles.variableChipEditor}
                            contentEditable={true}
                            dir="ltr"
                            data-placeholder="Write your message here... (type @ to mention variables)"
                            onInput={handleChipEditorInput}
                            onKeyDown={handleChipEditorKeyDown}
                            suppressContentEditableWarning={true}
                            style={{direction: 'ltr', unicodeBidi: 'bidi-override'}}
                        />
                        {showMentionDropdown && filteredMentions.length > 0 && (
                            <div
                                ref={dropdownRef}
                                className={styles.mentionDropdown}
                                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                            >
                                {filteredMentions.map((mention, index) => (
                                    <div
                                        key={index}
                                        className={`${styles.mentionItem} ${index === selectedMentionIndex ? styles.mentionItemSelected : ''}`}
                                        onClick={() => insertVariableChip(mention)}
                                        onMouseEnter={() => setSelectedMentionIndex(index)}
                                    >
                                        {mention.text}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        } else if (actionType === '5') {
            // MQTT Message
            return (
                <div>
                    <div className={styles.scratch_label}>{t('operations.actionNode.mqttConnection')}</div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                        <select
                            className={styles.modalSelect}
                            value={selectedMqttConnection || ''}
                            onChange={(event) => {
                                const connId = event.target.value ? parseInt(event.target.value) : null;
                                setSelectedMqttConnection(connId);
                                data.mqtt_connection_id = connId;
                            }}
                            style={{flex: 1}}
                        >
                            <option value="">{t('operations.actionNode.selectConnection')}</option>
                            {mqttConnections.map((conn) => (
                                <option key={conn.id} value={conn.id}>
                                    {conn.name} ({conn.broker}:{conn.port})
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => {
                                setConnectionManagerType('mqtt');
                                setShowConnectionManager(true);
                            }}
                            style={{
                                padding: '8px 12px',
                                backgroundColor: '#6366f1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            + New
                        </button>
                    </div>
                    {mqttConnections.length === 0 && (
                        <div style={{fontSize: '12px', color: '#94a3b8', marginTop: '4px'}}>
                            {t('operations.actionNode.noSavedConnections')}
                        </div>
                    )}

                    <div className={styles.scratch_label}>{t('operations.actionNode.device')}</div>
                    <select
                        className={styles.modalSelect}
                        value={selectedDeviceId || ''}
                        onChange={(event) => {
                            const deviceId = event.target.value;
                            setSelectedDeviceId(deviceId);
                            handleActionData({ target: { value: deviceId } }, 4);
                        }}
                    >
                        <option value="">{t('operations.actionNode.noneUseDeviceId')}</option>
                        {devices.map((device) => (
                            <option key={device.id} value={device.id}>
                                {device.name}
                            </option>
                        ))}
                    </select>
                    <div style={{fontSize: '11px', color: '#64748b', marginTop: '4px'}}>
                        {t('operations.actionNode.selectDeviceInfo')}
                    </div>

                    <div className={styles.scratch_label}>{t('operations.actionNode.topic')}</div>
                    <div className={styles.mentionContainer}>
                        <div
                            ref={topicEditorRef}
                            className={styles.variableChipEditor}
                            contentEditable={true}
                            dir="ltr"
                            data-placeholder="e.g., devices/@device_id/temperature"
                            onInput={(e) => {
                                setActiveEditor('topic');
                                setActiveDataIndex(1);
                                handleChipEditorInput(e);
                            }}
                            onKeyDown={handleChipEditorKeyDown}
                            suppressContentEditableWarning={true}
                            style={{direction: 'ltr', unicodeBidi: 'bidi-override', minHeight: '40px'}}
                        />
                        {showMentionDropdown && activeEditor === 'topic' && filteredMentions.length > 0 && (
                            <div
                                ref={dropdownRef}
                                className={styles.mentionDropdown}
                                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                            >
                                {filteredMentions.map((mention, index) => (
                                    <div
                                        key={index}
                                        className={`${styles.mentionItem} ${index === selectedMentionIndex ? styles.mentionItemSelected : ''}`}
                                        onClick={() => insertVariableChip(mention)}
                                        onMouseEnter={() => setSelectedMentionIndex(index)}
                                    >
                                        {mention.text}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px'}}>
                        <div className={styles.scratch_label} style={{marginBottom: 0}}>{t('operations.actionNode.message')}</div>
                        <button
                            type="button"
                            onClick={() => {
                                const text = htmlToText(chipEditorRef.current);
                                const tokens = {};
                                let i = 0;
                                const tokenized = text.replace(/@[^@\n,}\]"]+(\([^)]*\))?/g, m => {
                                    const k = `"__PH${i++}__"`;
                                    tokens[k] = m;
                                    return k;
                                });
                                try {
                                    const pretty = JSON.stringify(JSON.parse(tokenized), null, 2);
                                    const restored = pretty.replace(/"__PH\d+__"/g, m => tokens[m] || m);
                                    chipEditorRef.current.innerHTML = textToHtml(restored);
                                    setMqttJsonError(null);
                                } catch (_) {}
                            }}
                            disabled={!!mqttJsonError || !chipEditorRef.current?.textContent?.trim()}
                            style={{
                                fontSize: '11px', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer',
                                border: '1px solid #C7D2FE', background: '#EEF2FF', color: '#4F46E5',
                                fontWeight: 600, opacity: mqttJsonError ? 0.4 : 1,
                            }}
                        >
                            {t('operations.actionNode.formatJson')}
                        </button>
                    </div>
                    <div className={styles.mentionContainer}>
                        <div
                            ref={chipEditorRef}
                            className={styles.variableChipEditor}
                            contentEditable={true}
                            dir="ltr"
                            data-placeholder="Write your MQTT message... (type @ to mention variables, or use @device_id)"
                            onInput={(e) => {
                                setActiveEditor('message');
                                setActiveDataIndex(3);
                                handleChipEditorInput(e);
                                setMqttJsonError(tryParseJson(htmlToText(e.currentTarget)));
                            }}
                            onKeyDown={handleChipEditorKeyDown}
                            suppressContentEditableWarning={true}
                            style={{direction: 'ltr', unicodeBidi: 'bidi-override', border: mqttJsonError ? '1px solid #EF4444' : undefined, backgroundColor: mqttJsonError ? '#FFF8F8' : undefined}}
                        />
                        {showMentionDropdown && activeEditor === 'message' && filteredMentions.length > 0 && (
                            <div
                                ref={dropdownRef}
                                className={styles.mentionDropdown}
                                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                            >
                                {filteredMentions.map((mention, index) => (
                                    <div
                                        key={index}
                                        className={`${styles.mentionItem} ${index === selectedMentionIndex ? styles.mentionItemSelected : ''}`}
                                        onClick={() => insertVariableChip(mention)}
                                        onMouseEnter={() => setSelectedMentionIndex(index)}
                                    >
                                        {mention.text}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {mqttJsonError && (
                        <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>
                            ⚠ JSON inválido: {mqttJsonError}
                        </div>
                    )}

                    <div className={styles.scratch_label}>{t('operations.actionNode.booleanFormat')}</div>
                    <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px'}}>
                        {[{v: 'raw', l: '0 / 1'}, {v: 'bool', l: 'True / False'}, {v: 'lower', l: 'true / false'}].map(opt => (
                            <label key={opt.v} style={{display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer', padding: '3px 8px', borderRadius: '5px', border: `1px solid ${booleanFormat === opt.v ? '#4F46E5' : '#E2E8F0'}`, background: booleanFormat === opt.v ? '#EEF2FF' : '#F8FAFC', color: booleanFormat === opt.v ? '#4F46E5' : '#64748B', fontWeight: booleanFormat === opt.v ? 600 : 400}}>
                                <input type="radio" name="boolean_format_action" value={opt.v} checked={booleanFormat === opt.v} onChange={() => { setBooleanFormat(opt.v); data.boolean_format = opt.v; }} style={{accentColor: '#4F46E5'}}/>
                                {opt.l}
                            </label>
                        ))}
                    </div>
                </div>
            );
        } else if (actionType === '6') {
            // API Call
            return (
                <div>
                    <div className={styles.scratch_label}>{t('operations.actionNode.apiConnection')}</div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                        <select
                            className={styles.modalSelect}
                            value={selectedApiConnection || ''}
                            onChange={(event) => {
                                const connId = event.target.value ? parseInt(event.target.value) : null;
                                setSelectedApiConnection(connId);
                                data.api_connection_id = connId;
                            }}
                            style={{flex: 1}}
                        >
                            <option value="">{t('operations.actionNode.selectConnection')}</option>
                            {apiConnections.map((conn) => (
                                <option key={conn.id} value={conn.id}>
                                    {conn.name} ({conn.base_url})
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => {
                                setConnectionManagerType('api');
                                setShowConnectionManager(true);
                            }}
                            style={{
                                padding: '8px 12px',
                                backgroundColor: '#6366f1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            + New
                        </button>
                    </div>
                    {apiConnections.length === 0 && (
                        <div style={{fontSize: '12px', color: '#94a3b8', marginTop: '4px'}}>
                            {t('operations.actionNode.noSavedConnections')}
                        </div>
                    )}

                    <div className={styles.scratch_label}>{t('operations.actionNode.device')}</div>
                    <select
                        className={styles.modalSelect}
                        value={selectedDeviceId || ''}
                        onChange={(event) => {
                            const deviceId = event.target.value;
                            setSelectedDeviceId(deviceId);
                            handleActionData({ target: { value: deviceId } }, 4);
                        }}
                    >
                        <option value="">{t('operations.actionNode.noneUseDeviceIdEndpoint')}</option>
                        {devices.map((device) => (
                            <option key={device.id} value={device.id}>
                                {device.name}
                            </option>
                        ))}
                    </select>
                    <div style={{fontSize: '11px', color: '#64748b', marginTop: '4px'}}>
                        {t('operations.actionNode.selectDeviceInfoEndpoint')}
                    </div>

                    <div className={styles.scratch_label}>{t('operations.actionNode.httpMethod')}</div>
                    <select
                        className={styles.modalSelect}
                        value={actionData || 'GET'}
                        onChange={(event) => handleActionData(event, 1)}
                    >
                        {httpMethods.map((method) => (
                            <option key={method} value={method}>{method}</option>
                        ))}
                    </select>

                    <div className={styles.scratch_label}>{t('operations.actionNode.endpoint')}</div>
                    <div className={styles.mentionContainer}>
                        <div
                            ref={endpointEditorRef}
                            className={styles.variableChipEditor}
                            contentEditable={true}
                            dir="ltr"
                            data-placeholder="e.g., /devices/@device_id/alerts"
                            onInput={(e) => {
                                setActiveEditor('endpoint');
                                setActiveDataIndex(2);
                                handleChipEditorInput(e);
                            }}
                            onKeyDown={handleChipEditorKeyDown}
                            suppressContentEditableWarning={true}
                            style={{direction: 'ltr', unicodeBidi: 'bidi-override', minHeight: '40px'}}
                        />
                        {showMentionDropdown && activeEditor === 'endpoint' && filteredMentions.length > 0 && (
                            <div
                                ref={dropdownRef}
                                className={styles.mentionDropdown}
                                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                            >
                                {filteredMentions.map((mention, index) => (
                                    <div
                                        key={index}
                                        className={`${styles.mentionItem} ${index === selectedMentionIndex ? styles.mentionItemSelected : ''}`}
                                        onClick={() => insertVariableChip(mention)}
                                        onMouseEnter={() => setSelectedMentionIndex(index)}
                                    >
                                        {mention.text}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px'}}>
                        <div className={styles.scratch_label} style={{marginBottom: 0}}>{t('operations.actionNode.requestBody')}</div>
                        <button
                            type="button"
                            onClick={() => {
                                const text = htmlToText(apiBodyEditorRef.current);
                                const tokens = {};
                                let i = 0;
                                const tokenized = text.replace(/@[^@\n,}\]"]+(\([^)]*\))?/g, m => {
                                    const k = `"__PH${i++}__"`;
                                    tokens[k] = m;
                                    return k;
                                });
                                try {
                                    const pretty = JSON.stringify(JSON.parse(tokenized), null, 2);
                                    const restored = pretty.replace(/"__PH\d+__"/g, m => tokens[m] || m);
                                    apiBodyEditorRef.current.innerHTML = textToHtml(restored);
                                    setApiBodyJsonError(null);
                                } catch (_) {}
                            }}
                            disabled={!!apiBodyJsonError || !apiBodyEditorRef.current?.textContent?.trim()}
                            style={{
                                fontSize: '11px', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer',
                                border: '1px solid #C7D2FE', background: '#EEF2FF', color: '#4F46E5',
                                fontWeight: 600, opacity: apiBodyJsonError ? 0.4 : 1,
                            }}
                        >
                            {t('operations.actionNode.formatJson')}
                        </button>
                    </div>
                    <div className={styles.mentionContainer}>
                        <div
                            ref={apiBodyEditorRef}
                            className={styles.variableChipEditor}
                            contentEditable={true}
                            dir="ltr"
                            data-placeholder='{"alert": "value exceeded", "device": "@device_id", "value": @Variable (id)}... (type @ to mention variables)'
                            onInput={(e) => {
                                setActiveEditor('apiBody');
                                setActiveDataIndex(3);
                                handleChipEditorInput(e);
                                setApiBodyJsonError(tryParseJson(htmlToText(e.currentTarget)));
                            }}
                            onKeyDown={handleChipEditorKeyDown}
                            suppressContentEditableWarning={true}
                            style={{minHeight: '80px', fontFamily: 'monospace', direction: 'ltr', unicodeBidi: 'bidi-override', border: apiBodyJsonError ? '1px solid #EF4444' : undefined, backgroundColor: apiBodyJsonError ? '#FFF8F8' : undefined}}
                        />
                        {showMentionDropdown && activeEditor === 'apiBody' && filteredMentions.length > 0 && (
                            <div
                                ref={dropdownRef}
                                className={styles.mentionDropdown}
                                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                            >
                                {filteredMentions.map((mention, index) => (
                                    <div
                                        key={index}
                                        className={`${styles.mentionItem} ${index === selectedMentionIndex ? styles.mentionItemSelected : ''}`}
                                        onClick={() => insertVariableChip(mention)}
                                        onMouseEnter={() => setSelectedMentionIndex(index)}
                                    >
                                        {mention.text}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {apiBodyJsonError && (
                        <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>
                            ⚠ JSON inválido: {apiBodyJsonError}
                        </div>
                    )}
                    <div style={{fontSize: '11px', color: '#94a3b8', marginTop: '4px'}}>
                        {t('operations.actionNode.leaveEmptyGet')}
                    </div>

                    <div className={styles.scratch_label}>{t('operations.actionNode.booleanFormat')}</div>
                    <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px'}}>
                        {[{v: 'raw', l: '0 / 1'}, {v: 'bool', l: 'True / False'}, {v: 'lower', l: 'true / false'}].map(opt => (
                            <label key={opt.v} style={{display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer', padding: '3px 8px', borderRadius: '5px', border: `1px solid ${booleanFormat === opt.v ? '#4F46E5' : '#E2E8F0'}`, background: booleanFormat === opt.v ? '#EEF2FF' : '#F8FAFC', color: booleanFormat === opt.v ? '#4F46E5' : '#64748B', fontWeight: booleanFormat === opt.v ? 600 : 400}}>
                                <input type="radio" name="boolean_format_action" value={opt.v} checked={booleanFormat === opt.v} onChange={() => { setBooleanFormat(opt.v); data.boolean_format = opt.v; }} style={{accentColor: '#4F46E5'}}/>
                                {opt.l}
                            </label>
                        ))}
                    </div>
                </div>
            );
        } else {
            // Default: Email
            return (
                <div>
                    <div className={styles.scratch_label}>{t('operations.actionNode.to')}</div>
                    <input className={styles.scratch_input} placeholder="example@mail.com" onChange={(event) => {handleActionData(event, 1)}} value={actionData || ''}/>
                    <div className={styles.scratch_label}>{t('operations.actionNode.subject')}</div>
                    <input className={styles.scratch_input} placeholder={t('operations.actionNode.subject')} onChange={(event) => {handleActionData(event, 2)}} value={actionData2 || ''}/>
                    <div className={styles.scratch_label}>{t('operations.actionNode.body')}</div>
                    <div className={styles.mentionContainer}>
                        <div
                            ref={chipEditorRef}
                            className={styles.variableChipEditor}
                            contentEditable={true}
                            dir="ltr"
                            data-placeholder="Write your email body here... (type @ to mention variables)"
                            onInput={handleChipEditorInput}
                            onKeyDown={handleChipEditorKeyDown}
                            suppressContentEditableWarning={true}
                            style={{direction: 'ltr', unicodeBidi: 'bidi-override'}}
                        />
                        {showMentionDropdown && filteredMentions.length > 0 && (
                            <div
                                ref={dropdownRef}
                                className={styles.mentionDropdown}
                                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                            >
                                {filteredMentions.map((mention, index) => (
                                    <div
                                        key={index}
                                        className={`${styles.mentionItem} ${index === selectedMentionIndex ? styles.mentionItemSelected : ''}`}
                                        onClick={() => insertVariableChip(mention)}
                                        onMouseEnter={() => setSelectedMentionIndex(index)}
                                    >
                                        {mention.text}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        }
    }

    const actionDescriptors = [
        {name: t('operations.actionNode.sendEmail'), description: t('operations.actionNode.sendEmailDesc')},
        {name: t('operations.actionNode.addToDashboard'), description: t('operations.actionNode.addToDashboardDesc')},
        {name: t('operations.actionNode.sendSms'), description: t('operations.actionNode.sendSmsDesc')},
        {name: t('operations.actionNode.sendWhatsapp'), description: t('operations.actionNode.sendWhatsappDesc')},
        {name: t('operations.actionNode.sendMqtt'), description: t('operations.actionNode.sendMqttDesc')},
        {name: t('operations.actionNode.apiCall'), description: t('operations.actionNode.apiCallDesc')},
    ];

    // Get the help text for the current action type
    const currentHelpText = ACTION_HELP_TEXT[actionType];

    // Check if current action requires condition
    const needsCondition = NOTIFICATION_ACTIONS.includes(actionType);

    // Handle clicking delete - show confirmation if widget exists
    const handleTest = (e) => {
        e.stopPropagation();
        if (!data.id) return;
        setTesting(true);
        setTestResult(null);
        axios.post(`action/${data.id}/test/`).then(res => {
            setTesting(false);
            setTestResult({success: true});
            setTimeout(() => setTestResult(null), 3000);
        }).catch(err => {
            setTesting(false);
            setTestResult({success: false, message: err.response?.data?.error || 'Failed'});
            setTimeout(() => setTestResult(null), 3000);
        });
    };

    const handleDeleteClick = () => {
        if (data.action_data_5 && data.action_type === '2') {
            // Has associated widget, show confirmation modal
            setShowDeleteModal(true);
        } else {
            // No widget, delete directly
            deleteNode();
        }
    };

    // Actually delete the node (and widget if exists)
    const deleteNode = () => {
        if (typeof data.onDeleteNode === 'function') {
            data.onDeleteNode(id);
        } else {
            data.setNodes((prev) => [...prev.filter((node) => node.id !== id)]);
            data.setDeletionList((prev) => [...prev, id]);
        }
    };

    // Confirm deletion with widget
    const confirmDeleteWithWidget = () => {
        setDeleting(true);
        axios.delete(`dashboard_widget/${data.action_data_5}/`)
            .then(() => {
                console.log('Widget deleted successfully:', data.action_data_5);
                deleteNode();
                setShowDeleteModal(false);
                setDeleting(false);
            })
            .catch((err) => {
                console.error('Error deleting widget (may already be deleted):', err);
                // Still delete the action node even if widget deletion fails (widget may already be deleted)
                deleteNode();
                setShowDeleteModal(false);
                setDeleting(false);
            });
    };

    const handleSave = () => {
        // Guardar el contenido de los editores contentEditable antes de cerrar
        // Usar htmlToText para preservar el formato @Variable (id)
        if (chipEditorRef.current) {
            const text = htmlToText(chipEditorRef.current);
            handleActionData({ target: { value: text } }, 3);
        }
        if (apiBodyEditorRef.current) {
            const text = htmlToText(apiBodyEditorRef.current);
            handleActionData({ target: { value: text } }, 3);
        }
        if (topicEditorRef.current) {
            const text = htmlToText(topicEditorRef.current);
            handleActionData({ target: { value: text } }, 1);
        }
        if (endpointEditorRef.current) {
            const text = htmlToText(endpointEditorRef.current);
            handleActionData({ target: { value: text } }, 2);
        }

        setWidgetError('');
        setWidgetAdded(false);

        // Si es Add to Dashboard, crear el widget inmediatamente
        if (actionType === '2') {
            if (!actionData) {
                setWidgetError(t('operations.actionNode.pleaseSelectDashboard'));
                return;
            }
            if (!actionData4) {
                setWidgetError(t('operations.actionNode.pleaseSelectVariable'));
                return;
            }
            if (!actionData3) {
                setWidgetError(t('operations.actionNode.pleaseEnterWidgetName'));
                return;
            }

            // actionData = dashboard_id
            // actionData2 = widget type ('variable' o 'graph')
            // actionData3 = widget name
            // actionData4 = variable_id (puede ser número o "result_<id>" para variables calculadas)
            // actionData5 = widget_pk (si ya existe un widget creado)
            const widgetType = actionData2 === 'graph' ? 3 : 2; // 3 = graph, 2 = variable

            // Si es una variable calculada (result_), enviar como string; si no, como número
            const isResultVariable = String(actionData4).startsWith('result_');
            const keyValue = isResultVariable ? actionData4 : parseInt(actionData4);

            // Si ya existe un widget asociado, actualizarlo en lugar de crear uno nuevo
            if (actionData5) {
                const updatePayload = {
                    "name": actionData3,
                    "key": keyValue,
                    "type": widgetType,
                    "dashboard": parseInt(actionData)
                };
                // Include sampling only for graph widgets
                if (widgetType === 3 && actionData6 !== null && actionData6 !== undefined && actionData6 !== '') {
                    updatePayload.widget_sampling = parseInt(actionData6);
                }
                axios.put(`dashboard_widget/${actionData5}/`, updatePayload).then((res) => {
                    console.log('Widget updated:', res.data);
                    setWidgetUpdated(true);
                    setTimeout(() => {
                        setShowModal(false);
                        setWidgetUpdated(false);
                    }, 1500);
                }).catch((err) => {
                    console.error('Error updating widget:', err);
                    setWidgetError(t('operations.actionNode.errorUpdatingWidget'));
                });
            } else {
                // Crear nuevo widget
                const createPayload = {
                    "name": actionData3,
                    "key": keyValue,
                    "type": widgetType
                };
                // Include sampling only for graph widgets
                if (widgetType === 3 && actionData6 !== null && actionData6 !== undefined && actionData6 !== '') {
                    createPayload.widget_sampling = parseInt(actionData6);
                }
                axios.post(`dashboard/${actionData}/add_widget/`, createPayload).then((res) => {
                    console.log('Widget added to dashboard:', res.data);
                    // Guardar el widget_pk para futuras actualizaciones
                    const widgetPk = res.data.widget_pk || res.data.id || res.data.pk;
                    if (widgetPk) {
                        setActionData5(widgetPk);
                        data.action_data_5 = widgetPk;
                    }
                    setWidgetAdded(true);
                    setTimeout(() => {
                        setShowModal(false);
                        setWidgetAdded(false);
                    }, 1500);
                }).catch((err) => {
                    console.error('Error adding widget to dashboard:', err);
                    setWidgetError(t('operations.actionNode.errorAddingWidget'));
                });
            }
        } else {
            setShowModal(false);
        }
    }

    return (
        <div className={`${styles.scratch_block_action} shadow`} onContextMenu={(event) => {
            event.preventDefault();
            setShowModal(true)
        }}>
            <Explorer show={showDeviceExplorer} setter={setShowDeviceExplorer} handleActionData={handleActionData} actionData={actionData}/>

            <Modal show={showModal} setter={setShowModal} name={t('operations.nodes.configureAction')} buttonName={t('common.save')} onClick={handleSave}>
                <div className={'mb-3'}>
                    <select className={`${styles.modalSelect} mt-2`} onChange={(event) => {handleActionChange(event)}} value={actionType}>
                        <option value={'1'}>{t('operations.actionNode.sendEmail')}</option>
                        <option value={'2'}>{t('operations.actionNode.addToDashboard')}</option>
                        {systemSettings?.sms_enabled && <option value={'3'}>{t('operations.actionNode.sendSms')}</option>}
                        {systemSettings?.whatsapp_enabled && <option value={'4'}>{t('operations.actionNode.sendWhatsapp')}</option>}
                        <option value={'5'}>{t('operations.actionNode.sendMqtt')}</option>
                        <option value={'6'}>{t('operations.actionNode.apiCall')}</option>
                    </select>

                    <div className={styles.actionType}>
                        <div>{actionDescriptors[parseInt(actionType) - 1]?.name}</div>
                        <div>{actionDescriptors[parseInt(actionType) - 1]?.description}</div>
                    </div>

                    {/* Show requirement info for notification actions */}
                    {needsCondition && (
                        <div className={styles.actionRequirement}>
                            <div className={styles.actionRequirementHeader}>
                                <FontAwesomeIcon icon={faCircleInfo} />
                                <span>{t('operations.nodes.requirementLabel')}</span>
                            </div>
                            <div>{t('operations.actionNode.notificationRequirement')}</div>
                        </div>
                    )}

                    {/* Show anti-spam warning for notification actions */}
                    {needsCondition && (
                        <div className={styles.actionAntiSpamWarning}>
                            <div className={styles.actionAntiSpamHeader}>
                                <FontAwesomeIcon icon={faShieldHalved} />
                                <span>{t('operations.nodes.antiSpamProtection')}</span>
                            </div>
                            <div>{t(actionType === '3' ? 'operations.actionNode.antiSpamWarningSms' : actionType === '4' ? 'operations.actionNode.antiSpamWarningWhatsapp' : actionType === '5' ? 'operations.actionNode.antiSpamWarningMqtt' : actionType === '6' ? 'operations.actionNode.antiSpamWarningApi' : 'operations.actionNode.antiSpamWarning')}</div>
                        </div>
                    )}

                    {/* Show validation error if not connected to comparison operation */}
                    {!validationStatus.valid && (
                        <div className={styles.actionValidationError}>
                            <div className={styles.actionValidationErrorHeader}>
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                <span>{t('operations.nodes.configurationRequired')}</span>
                            </div>
                            <div>{validationStatus.message}</div>
                        </div>
                    )}

                    {inputs()}
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                show={showDeleteModal}
                setter={setShowDeleteModal}
                name={t('operations.nodes.deleteAction')}
                buttonName={deleting ? t('operations.nodes.deleting') : t('common.delete')}
                onClick={confirmDeleteWithWidget}
                danger={true}
            >
                <div style={{textAlign: 'center', padding: '10px 0'}}>
                    <FontAwesomeIcon icon={faTriangleExclamation} size="2x" color="#EF4444" style={{marginBottom: '15px'}}/>
                    <p style={{marginBottom: '10px', fontWeight: 'bold', color: '#334155'}}>
                        {t('operations.nodes.widgetHasOnDashboard')}
                    </p>
                    <p style={{color: '#64748B', fontSize: '14px'}}>
                        {t('operations.nodes.deletingWillRemoveWidget')}
                    </p>
                    <p style={{color: '#EF4444', fontSize: '14px', marginTop: '10px', fontWeight: '500'}}>
                        {t('operations.nodes.irreversible')}
                    </p>
                </div>
            </Modal>

            {/* Connection Manager Modal */}
            <ConnectionManager
                show={showConnectionManager}
                setter={setShowConnectionManager}
                type={connectionManagerType}
                projectId={project.id}
                onConnectionCreated={(newConnection) => {
                    if (connectionManagerType === 'mqtt') {
                        axios.get(`mqtt_connection/?project_id=${project.id}`).then((response) => {
                            setMqttConnections(response.data.results || response.data || []);
                            setSelectedMqttConnection(newConnection.id);
                            data.mqtt_connection_id = newConnection.id;
                        });
                    } else if (connectionManagerType === 'api') {
                        axios.get(`api_connection/?project_id=${project.id}`).then((response) => {
                            setApiConnections(response.data.results || response.data || []);
                            setSelectedApiConnection(newConnection.id);
                            data.api_connection_id = newConnection.id;
                        });
                    }
                }}
            />

            <Handle type="target" position={Position.Top} id="input"/>
            <div className="d-flex flex-row w-100 justify-content-between">
                <div className={'d-flex'}>
                    <div className={styles.scratch_block_type_icon_action}>
                        <FontAwesomeIcon icon={faPaperPlane} color="white"/>
                    </div>
                    <div className={styles.scratch_block_type}>{t('operations.nodes.action')}</div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    {corporateGroup?.dev_mode && data.id && parseInt(actionType) !== 2 && (
                        <div style={{cursor: 'pointer', padding: '0 4px'}} onClick={handleTest} title="Test action">
                            {testing ? (
                                <FontAwesomeIcon icon={faSpinner} spin color="#F59E0B" size="sm"/>
                            ) : testResult ? (
                                <FontAwesomeIcon icon={testResult.success ? faCheck : faTriangleExclamation} color={testResult.success ? '#22C55E' : '#EF4444'} size="sm"/>
                            ) : (
                                <FontAwesomeIcon icon={faPlay} color="#F59E0B" size="sm"/>
                            )}
                        </div>
                    )}
                    <div className={'px-2 pointer-event'} onClick={handleDeleteClick}>
                        <FontAwesomeIcon icon={faX} color={'#999'} size={'sm'}/>
                    </div>
                </div>
            </div>

            <div className={'pt-2'}>{actionDescriptors[parseInt(actionType) - 1]?.name}</div>

            {/* Show validation warning indicator on the node */}
            {!validationStatus.valid && needsCondition && (
                <div className={styles.nodeValidationWarning} title={validationStatus.message}>
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <span>{t('operations.nodes.requiresConditionLabel')}</span>
                </div>
            )}

            {/* Output handle for API actions - allows flow to continue after API call */}
            {actionType === '6' && (
                <>
                    <div style={{ fontSize: '10px', color: '#64748B', textAlign: 'center', marginTop: '4px' }}>
                        Status Code ↓
                    </div>
                    <Handle type="source" position={Position.Bottom} id="out_api" />
                </>
            )}
        </div>
    )
}